import type { CSSProperties } from 'react';
import {
  RectangleStackIcon,
  TableCellsIcon,
  Squares2X2Icon,
  ArrowDownTrayIcon,
} from '@heroicons/react/16/solid';
import { SaveDropdownProps } from '../types';
import { DOWNLOAD_EXTENSIONS } from '../../../constants';

const dropdownItemStyle = (disabled?: boolean): CSSProperties => ({
  width: '100%',
  padding: '12px 16px',
  border: 'none',
  background: 'white',
  textAlign: 'left',
  cursor: disabled ? 'not-allowed' : 'pointer',
  fontSize: '14px',
  transition: 'background-color 0.2s',
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  opacity: disabled ? 0.5 : 1,
});

export function SaveDropdown({
  hasSearchItems,
  hasFilters,
  hasCustomColumns,
  sourceWeights,
  tab,
  view,
  onSaveComplete,
  onSaveColumnSelection,
  onSaveScore,
  onDownload,
  showEnrichmentTrigger,
  enrichmentSelectionMode,
  onEnrichmentAction,
  showSaveOptions = true,
}: SaveDropdownProps) {
  return (
    <div
      style={{
        position: 'absolute',
        top: '100%',
        right: 0,
        marginTop: '4px',
        background: 'white',
        border: '1px solid #ddd',
        borderRadius: '6px',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        zIndex: 1000,
        minWidth: '240px',
        overflow: 'hidden',
      }}
    >
      {showEnrichmentTrigger && onEnrichmentAction && (
        <>
          <button
            type="button"
            onClick={onEnrichmentAction}
            style={dropdownItemStyle(false)}
            onMouseEnter={e => {
              e.currentTarget.style.backgroundColor = '#f5f5f5';
            }}
            onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'white')}
          >
            <Squares2X2Icon className="w-5 h-5" />
            <div>
              <div style={{ fontWeight: '600', marginBottom: '2px' }}>
                {enrichmentSelectionMode ? 'Cancel selection' : 'Select for enrichment'}
              </div>
              <div style={{ fontSize: '12px', color: '#666' }}>
                {enrichmentSelectionMode ? 'Exit selection mode' : 'Select rows and run enrichment'}
              </div>
            </div>
          </button>
          <div style={{ borderTop: '1px solid #eee' }} />
        </>
      )}

      {showSaveOptions && (
        <>
      <button
        type="button"
        onClick={onSaveComplete}
        disabled={!hasSearchItems && !hasFilters && !hasCustomColumns}
        style={dropdownItemStyle(!hasSearchItems && !hasFilters && !hasCustomColumns)}
        onMouseEnter={e => {
          if (hasSearchItems || hasFilters || hasCustomColumns) {
            e.currentTarget.style.backgroundColor = '#f5f5f5';
          }
        }}
        onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'white')}
      >
        <RectangleStackIcon className="w-5 h-5" />
        <div>
          <div style={{ fontWeight: '600', marginBottom: '2px' }}>Save search</div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            {!hasSearchItems && !hasFilters && !hasCustomColumns ? 'No search to save' : 'Save current view'}
          </div>
        </div>
      </button>

      <div style={{ borderTop: '1px solid #eee' }} />

      <button
        type="button"
        onClick={onSaveColumnSelection}
        disabled={!hasCustomColumns}
        style={dropdownItemStyle(!hasCustomColumns)}
        onMouseEnter={e => {
          if (hasCustomColumns) {
            e.currentTarget.style.backgroundColor = '#f5f5f5';
          }
        }}
        onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'white')}
      >
        <TableCellsIcon className="w-5 h-5" />
        <div>
          <div style={{ fontWeight: '600', marginBottom: '2px' }}>Columns Only</div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            {!hasCustomColumns ? 'No columns customized' : 'Visibility + order'}
          </div>
        </div>
      </button>

      {['GDA', 'VDA'].includes(tab) && sourceWeights.length > 0 && (
        <>
          <div style={{ borderTop: '1px solid #eee' }} />
          <button
            type="button"
            onClick={onSaveScore}
            style={dropdownItemStyle(false)}
            onMouseEnter={e => {
              e.currentTarget.style.backgroundColor = '#f5f5f5';
            }}
            onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'white')}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
              />
            </svg>
            <div>
              <div style={{ fontWeight: '600', marginBottom: '2px' }}>Score Weights</div>
              <div style={{ fontSize: '12px', color: '#666' }}>Save custom source scores</div>
            </div>
          </button>
        </>
      )}
        </>
      )}

      {view !== 'ALL' && (
        <>
          <div style={{ borderTop: '1px solid #eee' }} />
          {DOWNLOAD_EXTENSIONS.map((extension, index) => (
            <button
              key={extension.value || index}
              type="button"
              onClick={() => onDownload(extension)}
              style={dropdownItemStyle(false)}
              onMouseEnter={e => {
                e.currentTarget.style.backgroundColor = '#f5f5f5';
              }}
              onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'white')}
            >
              <ArrowDownTrayIcon className="w-5 h-5" />
              <div>
                <div style={{ fontWeight: '600', marginBottom: '2px' }}>{extension.label}</div>
                <div style={{ fontSize: '12px', color: '#666' }}>
                  Download results as {extension.label}
                </div>
              </div>
            </button>
          ))}
        </>
      )}
    </div>
  );
}
